import { DataGrid, MUIAccordion, MUIButton, MUICheckBox } from "@abb/arcadia-ui-react"
import { StatusCodes } from "http-status-codes"
import { MouseEvent, useCallback, useEffect, useState } from "react"
import { useFieldArray, useForm, useWatch } from "react-hook-form"
import { toast } from "react-toastify"
import { digitalManagerApi } from "../../../../services/api"
import { DiMaProgress } from "../../../components/DiMaProgress"
import { ApproverSendEmail, UserApproverGet } from "../../../models/User"

export function EmailSettings() {
  const [isLoadingRoles, setIsLoadingRoles] = useState<boolean>(true)
  const [approverRoles, setApproverRoles] = useState<UserApproverGet[]>([])

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting, isDirty },
  } = useForm<{ approverSendEmails: ApproverSendEmail[] }>({
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      approverSendEmails: [],
    })
  }, [reset])

  const { fields, replace } = useFieldArray({
    control,
    name: "approverSendEmails",
  })

  useEffect(() => {
    digitalManagerApi
      .get<UserApproverGet[]>(`/users/currentUser/approvers`)
      .then((res) => {
        if (res.status === StatusCodes.OK) {
          setApproverRoles(res.data)
          setIsLoadingRoles(false)

          const tempApproverSendEmail: ApproverSendEmail[] = res.data.map((approver) => ({
            approverId: approver.id,
            sendEmail: approver.sendEmail,
          }))

          replace(tempApproverSendEmail)
        }
      })
      .catch(() => {
        toast.error("Cannot retrieve user's approver roles")
      })
  }, [replace])

  const watchedApproverSendEmails = useWatch({
    control,
    name: "approverSendEmails",
  })

  const handleSaveSettings = useCallback(
    async (data: { approverSendEmails: ApproverSendEmail[] }) => {
      try {
        await digitalManagerApi.put(`/users/currentUser/settings/approvers`, {
          approverSendEmails: data.approverSendEmails,
        })
        toast.success("User's email settings updated")
      } catch {
        toast.error("Cannot update user's email settings")
      }
    },
    []
  )

  const handleDisableAllSettings = useCallback(() => {
    fields.forEach((_, index) => {
      setValue(`approverSendEmails.${index}.sendEmail`, false)
    })
  }, [setValue, fields])

  const handleToggleSendEmail = useCallback(
    (index: number) => (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation()
      const currentValue = watchedApproverSendEmails?.[index]?.sendEmail || false
      setValue(`approverSendEmails.${index}.sendEmail`, !currentValue)
    },
    [setValue, watchedApproverSendEmails]
  )

  const approverRolesByApplication = approverRoles.reduce((acc, approverRole) => {
    const appName = approverRole.application.name
    if (!acc.has(appName)) {
      acc.set(appName, [])
    }
    acc.get(appName)?.push(approverRole)
    return acc
  }, new Map<string, UserApproverGet[]>())

  const headerData = [
    { id: "role", label: "Role", numeric: false, actions: false, enableSort: false },
    {
      id: "approverType",
      label: "Approver type",
      numeric: false,
      actions: false,
      enableSort: false,
    },
    {
      id: "properties",
      label: "Properties",
      numeric: false,
      actions: false,
      enableSort: false,
    },
    {
      id: "sendEmail",
      label: "Receive emails",
      numeric: false,
      actions: false,
      enableSort: false,
    },
  ]

  const handleCellClick = useCallback(
    (rowId: string, columnId: string) => {
      if (columnId === "sendEmail") {
        const index = fields.findIndex((field) => field.approverId === rowId)
        if (index !== -1) {
          const currentValue = watchedApproverSendEmails?.[index]?.sendEmail || false
          setValue(`approverSendEmails.${index}.sendEmail`, !currentValue)
        }
      }
    },
    [fields, watchedApproverSendEmails, setValue]
  )

  const accordionItems = Array.from(approverRolesByApplication.entries()).map(
    ([mapKey, approvers]) => {
      const sortedApprovers = approvers.toSorted((a, b) =>
        a.role.name.localeCompare(b.role.name)
      )

      const rows = sortedApprovers.map((appApproverRole) => {
        const index = fields.findIndex((field) => field.approverId === appApproverRole.id)

        return {
          id: appApproverRole.id,
          role: appApproverRole.role.name,
          approverType: appApproverRole.approverType,
          properties: appApproverRole.property.value
            ? `${appApproverRole.property.name}: ${appApproverRole.property.label}`
            : "Generic",
          sendEmail: (
            <MUICheckBox
              size={24}
              checked={watchedApproverSendEmails?.[index]?.sendEmail || false}
              handleToggle={handleToggleSendEmail(index)}
            />
          ),
        }
      })

      return {
        id: mapKey,
        headerLeftPart: <div>{mapKey}</div>,
        accordionContent: (
          <DataGrid
            page={0}
            name={`accordion-${mapKey}`}
            rowsPerPage={rows.length}
            size={40}
            rows={rows}
            headerData={headerData}
            enableCheckbox={false}
            onClickCell={handleCellClick}
            parentClassName="full-width-grid"
          />
        ),
        defaultState: "open" as "open" | "closed",
      }
    }
  )

  return (
    <>
      {isLoadingRoles && <DiMaProgress />}
      {!isLoadingRoles && (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "16px",
              alignItems: "center",
              justifyContent: "end",
              marginBottom: "16px",
              marginTop: "16px",
            }}
          >
            <MUIButton
              variant="primary"
              onClickButton={handleDisableAllSettings}
              label="Disable all"
              size={32}
            />
            <MUIButton
              label="Save"
              variant="primary"
              size={32}
              isLoading={isSubmitting}
              disabled={isSubmitting || !isDirty}
              onClickButton={handleSubmit(handleSaveSettings)}
            />
          </div>
          <MUIAccordion accordionItems={accordionItems} isExpandAllButtonPresent={true} />
        </>
      )}
    </>
  )
}
